import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { updateRole, getMe } from '../lib/api';
import { GraduationCap, BookUser, Keyboard } from 'lucide-react';
import { toast } from 'sonner';

const RoleSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    if (!user) {
      const fetchUser = async () => {
        try {
          const response = await getMe();
          setUser(response.data);
          // If user already has a role, redirect
          if (response.data.role === 'teacher') {
            navigate('/teacher');
          } else if (response.data.role === 'student') {
            navigate('/student');
          }
        } catch {
          navigate('/');
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [user, navigate]);

  const handleSelectRole = async (role) => {
    setSelecting(true);
    try {
      await updateRole(role);
      toast.success(`Welcome, ${role === 'teacher' ? 'Teacher' : 'Student'}!`);
      navigate(role === 'teacher' ? '/teacher' : '/student');
    } catch (error) {
      toast.error('Failed to set role');
      setSelecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center neon-border">
            <Keyboard className="w-6 h-6 text-primary" />
          </div>
          <span className="font-heading text-2xl font-bold">LitBlitz</span>
        </div>

        {/* Welcome */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl font-bold mb-2">Welcome, {user?.name}!</h1>
          <p className="text-text-secondary">Select your role to continue</p>
        </div>

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Teacher Card */}
          <Card 
            className="card-glass p-8 cursor-pointer group"
            onClick={() => !selecting && handleSelectRole('teacher')}
            data-testid="role-teacher-card"
          >
            <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center mb-6 group-hover:neon-border transition-all">
              <GraduationCap className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">I'm a Teacher</h2>
            <p className="text-text-secondary text-sm mb-6">
              Create classrooms, assign exercises, and track student progress.
            </p>
            <Button 
              className="btn-primary w-full rounded-sm"
              disabled={selecting}
              data-testid="select-teacher-btn"
            >
              {selecting ? 'Loading...' : 'Continue as Teacher'}
            </Button>
          </Card>

          {/* Student Card */}
          <Card 
            className="card-glass p-8 cursor-pointer group"
            onClick={() => !selecting && handleSelectRole('student')}
            data-testid="role-student-card"
          >
            <div className="w-16 h-16 rounded-xl bg-secondary/20 flex items-center justify-center mb-6 group-hover:neon-border-secondary transition-all">
              <BookUser className="w-8 h-8 text-secondary" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">I'm a Student</h2>
            <p className="text-text-secondary text-sm mb-6">
              Join classes, complete typing exercises, and track your progress.
            </p>
            <Button 
              className="bg-secondary hover:bg-secondary-hover text-white w-full rounded-sm"
              disabled={selecting}
              data-testid="select-student-btn"
            >
              {selecting ? 'Loading...' : 'Continue as Student'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RoleSelect;
